import { Dialog, FormStatus, P } from '@dnb/eufemia';
import { trash } from '@dnb/eufemia/icons';
import { useCallback, useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import { createScopeDeletionRequest } from '@/api/api';

import { ApiProviderPageOutletContext } from '../..';

interface RequestScopeDeletionProps {
  apiSlug: string;
  scopeName: string;
  onSuccess: () => void;
}

export default function RequestScopeDeletion({ apiSlug, scopeName, onSuccess }: RequestScopeDeletionProps) {
  const { isTeamAdmin, api } = useOutletContext<ApiProviderPageOutletContext>();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleDeleteScopeRequest = useCallback(async () => {
    setErrorMessage(null);
    const { error, response } = await createScopeDeletionRequest(apiSlug, { name: scopeName });
    if (!response.ok) {
      setErrorMessage(error?.detail ?? 'Failed to create scope deletion request. Please try again.');
      return false;
    }
    setDialogOpen(false);
    onSuccess();
    return true;
  }, [apiSlug, scopeName, onSuccess]);

  const hasPendingDeletionRequest = Boolean(
    api.scopeRequests?.some(
      (request) =>
        request.scopeName === scopeName && request.requestType === 'DELETE' && request.reviewStatus === 'PENDING',
    ),
  );

  const shouldDisable = !isTeamAdmin || hasPendingDeletionRequest;

  const tooltip = hasPendingDeletionRequest
    ? 'A pending deletion request already exists for this scope'
    : isTeamAdmin
      ? 'Request scope deletion'
      : 'Only team admins can request scope deletion';

  return (
    <Dialog
      confirmText="Submit Request"
      confirmType="warning"
      declineText="Cancel"
      description={
        <>
          <P>
            Request to delete scope <b>{scopeName}</b> will be submitted for review. If approved, the scope will be
            deleted.
          </P>
          {errorMessage && (
            <FormStatus stretch top="x-small">
              {errorMessage}
            </FormStatus>
          )}
        </>
      }
      icon={trash}
      noAnimation
      onClose={() => setDialogOpen(false)}
      onConfirm={async ({ close }) => {
        if (hasPendingDeletionRequest) {
          close();
          return;
        }
        const success = await handleDeleteScopeRequest();
        if (success) {
          close();
        }
      }}
      openState={dialogOpen}
      title="Request scope deletion?"
      triggerAttributes={{
        on_click: () => setDialogOpen(true),
        icon: trash,
        variant: 'tertiary',
        size: 'small',
        disabled: shouldDisable,
        tooltip,
      }}
      variant="confirmation"
    />
  );
}
